
import { useDispatch, useSelector } from "react-redux"
import { setShowItemDetails } from "../../features/ui/uiSlice"
import { addItem } from "../../features/cart/cartSlice"
import type { RootState } from "../../store"
import { X, Minus, Plus } from "lucide-react"
import { useState } from "react"
import { useTheme } from "../../contexts/ThemeContext"
import { useTranslation } from "../../hooks/useTranslation"
import CurrencyDisplay from "../ui/CurrencyDisplay"

export default function ItemDetailsModal() {
  const dispatch = useDispatch()
  const { showItemDetails, selectedItem } = useSelector((state: RootState) => state.ui)
  const { primaryColor, canOrder } = useTheme()
  const { t } = useTranslation()
  const [quantity, setQuantity] = useState(1)
  const [selectedSize, setSelectedSize] = useState(t("menu.singleSize"))
  const [selectedAddons, setSelectedAddons] = useState<string[]>([])

  if (!showItemDetails || !selectedItem) return null

  const handleClose = () => {
    dispatch(setShowItemDetails(false))
    setQuantity(1)
    setSelectedSize(t("menu.singleSize"))
    setSelectedAddons([])
  }

  const handleAddToCart = () => {
    dispatch(
      addItem({
        ...selectedItem,
        quantity,
        size: selectedSize,
        addons: selectedAddons,
      }),
    )
    handleClose()
  }

  const toggleAddon = (addon: string) => {
    setSelectedAddons((prev) => (prev.includes(addon) ? prev.filter((a) => a !== addon) : [...prev, addon]))
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50">
      <div className="bg-white w-full max-w-md rounded-t-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-4">
          <div className="flex justify-end items-center mb-4">
            <button onClick={handleClose} className="p-2">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="text-center mb-6">
            <img
              src={selectedItem.image || "/placeholder.svg"}
              alt={selectedItem.name}
              className="w-32 h-32 object-cover rounded-lg mx-auto mb-4"
            />
            <h2 className="text-xl font-bold mb-2">{selectedItem.name}</h2>
            <p className="text-gray-600 text-sm mb-4">
              قطعة اللحوم السويدية مع البرجر الكريمي وجبنة شيدر وجرجير اللحم الصافي
            </p>
            <div className="text-2xl font-bold flex items-center justify-center gap-1.5" style={{ color: primaryColor }} dir="ltr">
              <CurrencyDisplay src="/sar.svg" className="w-6 h-6" iconStyle={{ filter: `brightness(0) saturate(100%)`, color: primaryColor }} />
              <span>{selectedItem.price}</span>
            </div>
          </div>

          {/* Size Selection */}
          <div className="mb-6">
            <h3 className="font-bold mb-3">{t("menu.size")}</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setSelectedSize(t("menu.doubleSize"))}
                className={`p-3 rounded-lg border-2 ${selectedSize === t("menu.doubleSize") ? "border-current bg-opacity-10" : "border-gray-200"
                  }`}
                style={
                  selectedSize === t("menu.doubleSize") ? { borderColor: primaryColor, backgroundColor: `${primaryColor}20` } : {}
                }
              >
                <div className="text-center">
                  <div className="font-bold">{t("menu.doubleSize")}</div>
                  <div className="text-sm flex items-center justify-center gap-1.5" style={{ color: selectedSize === t("menu.doubleSize") ? primaryColor : '#6b7280' }} dir="ltr">
                    <CurrencyDisplay src="/sar.svg" className="w-3 h-3" iconStyle={{ filter: `brightness(0) saturate(100%)`, color: selectedSize === t("menu.doubleSize") ? primaryColor : '#6b7280' }} />
                    <span>364</span>
                  </div>
                </div>
              </button>
              <button
                onClick={() => setSelectedSize(t("menu.singleSize"))}
                className={`p-3 rounded-lg border-2 ${selectedSize === t("menu.singleSize") ? "border-current bg-opacity-10" : "border-gray-200"
                  }`}
                style={
                  selectedSize === t("menu.singleSize") ? { borderColor: primaryColor, backgroundColor: `${primaryColor}20` } : {}
                }
              >
                <div className="text-center">
                  <div className="font-bold">{t("menu.singleSize")}</div>
                  <div className="text-sm flex items-center justify-center gap-1.5" style={{ color: selectedSize === t("menu.singleSize") ? primaryColor : '#6b7280' }} dir="ltr">
                    <CurrencyDisplay src="/sar.svg" className="w-3 h-3" iconStyle={{ filter: `brightness(0) saturate(100%)`, color: selectedSize === t("menu.singleSize") ? primaryColor : '#6b7280' }} />
                    <span>260</span>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Addons */}
          <div className="mb-6">
            <h3 className="font-bold mb-3">{t("menu.addons")}</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => toggleAddon(t("menu.addPickle"))}
                className={`p-3 rounded-lg border-2 ${selectedAddons.includes(t("menu.addPickle")) ? "border-current bg-opacity-10" : "border-gray-200"
                  }`}
                style={
                  selectedAddons.includes(t("menu.addPickle"))
                    ? { borderColor: primaryColor, backgroundColor: `${primaryColor}20` }
                    : {}
                }
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg mx-auto mb-2"></div>
                  <div className="font-bold text-sm">{t("menu.addPickle")}</div>
                  <div className="text-xs flex items-center justify-center gap-1.5" style={{ color: selectedAddons.includes(t("menu.addPickle")) ? primaryColor : '#6b7280' }} dir="ltr">
                    <CurrencyDisplay src="/sar.svg" className="w-3 h-3" iconStyle={{ filter: `brightness(0) saturate(100%)`, color: selectedAddons.includes(t("menu.addPickle")) ? primaryColor : '#6b7280' }} />
                    <span>16</span>
                  </div>
                </div>
              </button>
              <button
                onClick={() => toggleAddon(t("menu.addCheese"))}
                className={`p-3 rounded-lg border-2 ${selectedAddons.includes(t("menu.addCheese")) ? "border-current bg-opacity-10" : "border-gray-200"
                  }`}
                style={
                  selectedAddons.includes(t("menu.addCheese"))
                    ? { borderColor: primaryColor, backgroundColor: `${primaryColor}20` }
                    : {}
                }
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg mx-auto mb-2"></div>
                  <div className="font-bold text-sm">{t("menu.addCheese")}</div>
                  <div className="text-xs flex items-center justify-center gap-1.5" style={{ color: selectedAddons.includes(t("menu.addCheese")) ? primaryColor : '#6b7280' }} dir="ltr">
                    <CurrencyDisplay src="/sar.svg" className="w-3 h-3" iconStyle={{ filter: `brightness(0) saturate(100%)`, color: selectedAddons.includes(t("menu.addCheese")) ? primaryColor : '#6b7280' }} />
                    <span>16</span>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Quantity and Add to Cart */}
          {canOrder && (
          <>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="text-lg font-bold">{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <span className="text-lg font-bold flex items-center gap-1.5" style={{ color: primaryColor }} dir="ltr">
              <CurrencyDisplay src="/sar.svg" className="w-5 h-5" iconStyle={{ filter: `brightness(0) saturate(100%)`, color: primaryColor }} />
              <span>{selectedItem.price * quantity}</span>
            </span>
          </div>

          <button
            onClick={handleAddToCart}
            className="w-full py-3 rounded-lg text-white font-bold"
            style={{ backgroundColor: primaryColor }}
          >
            {t("menu.addToCart")}
          </button>
          </>
          )}
        </div>
      </div>
    </div>
  )
}
